/* eslint-disable @typescript-eslint/camelcase */
import { ManagerUsersActionType, ManagerUsersAction } from '../actions/managerUsersActions';
import { ManagerUser, Shop } from '@card/types';

export interface ManagersState {
  managerUsers: ManagerUser[];
  managerUsersFiltered: ManagerUser[];
  searchWord: string;
  searchShopsWord: string;
  loadingManagerUsers: boolean;
  currentManagerUser: ManagerUser;
  currentManagerUserShops: ShopFormatedType[];
  currentManagerUserShopsFiltered: ShopFormatedType[];
  allShopsAssigned: boolean;
  savingManagerUser: boolean;
  returnToUsersList: boolean;
}

export type ShopFormatedType = {
  shop: {
    id: string;
    name: string;
  };
  isAssigned: boolean;
};

export const currentManagerUserInitial: ManagerUser = {
  id: '',
  first_name: '',
  last_name: '',
  shops: [],
  email: '',
  account_administrator: false,
  validated_account: true
};

const initialState: ManagersState = {
  managerUsers: [],
  managerUsersFiltered: [],
  searchWord: '',
  loadingManagerUsers: true,
  currentManagerUserShops: [],
  currentManagerUser: currentManagerUserInitial,
  currentManagerUserShopsFiltered: [],
  searchShopsWord: '',
  allShopsAssigned: false,
  savingManagerUser: false,
  returnToUsersList: false
};

function filterUsers(searchWord: string, state: ManagersState): ManagerUser[] {
  return state.managerUsers.filter((mu) => {
    return (
      mu.first_name.toUpperCase().includes(searchWord.toUpperCase()) ||
      mu.last_name.toUpperCase().includes(searchWord.toUpperCase()) ||
      mu.email.toUpperCase().includes(searchWord.toUpperCase())
    );
  });
}

function filterShops(
  searchWord: string,
  currentManagerUserShops: ShopFormatedType[]
): ShopFormatedType[] {
  return currentManagerUserShops.filter((muShop) =>
    muShop.shop.name.toUpperCase().includes(searchWord.toUpperCase())
  );
}

// This recieve an object with type: {ManagerUsers from api, Shops from api}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getManagerUsersformated(apiResponse: any): ManagerUser[] {
  const { managerUsers }: { managerUsers: ManagerUser[] } = apiResponse;
  return managerUsers;
}

// This recieve an object with type: {Shops from api}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getCurrentManagerUserShopsformated(shops: Shop[]): ShopFormatedType[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return shops.map((shop) => {
    return {
      shop,
      isAssigned: false
    };
  });
}

function getCurrentManagerUserShops(
  state: ManagersState,
  currentManagerUser: ManagerUser
): ShopFormatedType[] {
  return state.currentManagerUserShops.map((shopFromState) => {
    return {
      shop: shopFromState.shop,
      isAssigned: currentManagerUser
        ? currentManagerUser.shops.filter(
            (shop: { id: string; name: string }) => shop.id === shopFromState.shop.id
          ).length > 0
        : false
    };
  });
}

function toggleAllFilteredShops(
  currentManagerUserShops: ShopFormatedType[],
  searchShopsWord: string,
  assignShops: boolean
): ShopFormatedType[] {
  return currentManagerUserShops.map((shopFromAllShopsList) => {
    return {
      shop: shopFromAllShopsList.shop,
      isAssigned:
        shopFromAllShopsList.shop.name.toUpperCase().search(searchShopsWord.toUpperCase()) === -1
          ? shopFromAllShopsList.isAssigned
          : assignShops
    };
  });
}

function toggleSelectedShop(
  currentManagerUserShops: ShopFormatedType[],
  shopSelected: ShopFormatedType
): ShopFormatedType[] {
  return currentManagerUserShops.map((shopFromAllShopsList) => {
    return {
      shop: shopFromAllShopsList.shop,
      isAssigned:
        shopFromAllShopsList.shop.id.search(shopSelected.shop.id) === -1
          ? shopFromAllShopsList.isAssigned
          : shopSelected.isAssigned
    };
  });
}

function allShopsSelected(shopsToEvaluate: ShopFormatedType[]): boolean {
  let allAssigned = true;
  let count = 0;
  while (allAssigned && count < shopsToEvaluate.length) {
    allAssigned = shopsToEvaluate[count].isAssigned;
    count++;
  }
  return allAssigned;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const managerUsersReducers = function(
  state = initialState,
  action: ManagerUsersAction
): ManagersState {
  switch (action.type) {
    case ManagerUsersActionType.Get:
      return {
        ...state,
        loadingManagerUsers: true
      };
    case ManagerUsersActionType.GetSuccess: {
      const managerUsersFormated: ManagerUser[] = getManagerUsersformated(action.payload);
      const currentManagerUserShops: ShopFormatedType[] = getCurrentManagerUserShopsformated(
        action.payload.shops
      );
      return {
        ...state,
        loadingManagerUsers: false,
        managerUsersFiltered: managerUsersFormated,
        managerUsers: managerUsersFormated,
        currentManagerUserShops: currentManagerUserShops
      };
    }
    case ManagerUsersActionType.FilterUsers:
      return {
        ...state,
        searchWord: action.payload,
        managerUsersFiltered: filterUsers(action.payload, state)
      };
    case ManagerUsersActionType.FilterShops: {
      const currentManagerUserShopsFiltered: ShopFormatedType[] = filterShops(
        action.payload,
        state.currentManagerUserShops
      );
      const allShopsAssigned = allShopsSelected(currentManagerUserShopsFiltered);
      return {
        ...state,
        searchShopsWord: action.payload,
        currentManagerUserShopsFiltered: currentManagerUserShopsFiltered,
        allShopsAssigned: allShopsAssigned
      };
    }
    case ManagerUsersActionType.SetCurrentUserToEdit: {
      const currentManagerUserShops: ShopFormatedType[] = getCurrentManagerUserShops(
        state,
        action.payload
      );
      return {
        ...state,
        savingManagerUser: false,
        returnToUsersList: false,
        currentManagerUser: action.payload,
        currentManagerUserShops: currentManagerUserShops,
        currentManagerUserShopsFiltered: currentManagerUserShops,
        allShopsAssigned: state.currentManagerUserShops.length === action.payload.shops.length
      };
    }
    case ManagerUsersActionType.SaveShop: {
      const currentManagerUserShops = toggleSelectedShop(
        state.currentManagerUserShops,
        action.payload
      );
      const currentManagerUserShopsFiltered = filterShops(
        state.searchShopsWord,
        currentManagerUserShops
      );
      return {
        ...state,
        currentManagerUserShops: currentManagerUserShops,
        currentManagerUserShopsFiltered: currentManagerUserShopsFiltered,
        allShopsAssigned: allShopsSelected(currentManagerUserShopsFiltered)
      };
    }
    case ManagerUsersActionType.ChangeUserType:
      return {
        ...state,
        currentManagerUser: {
          ...state.currentManagerUser,
          account_administrator: !state.currentManagerUser.account_administrator
        }
      };
    case ManagerUsersActionType.SelectAll: {
      const currentManagerUserShops = toggleAllFilteredShops(
        state.currentManagerUserShops,
        state.searchShopsWord,
        !state.allShopsAssigned
      );
      const currentManagerUserShopsFiltered = filterShops(
        state.searchShopsWord,
        currentManagerUserShops
      );
      return {
        ...state,
        currentManagerUserShops: currentManagerUserShops,
        currentManagerUserShopsFiltered: currentManagerUserShopsFiltered,
        allShopsAssigned: allShopsSelected(currentManagerUserShopsFiltered)
      };
    }
    case ManagerUsersActionType.SaveManagerUser:
      return {
        ...state,
        savingManagerUser: true
      };
    case ManagerUsersActionType.SaveManagerUserSuccess:
      return {
        ...state,
        returnToUsersList: true,
        savingManagerUser: false
      };
    default:
      return state;
  }
};
export default managerUsersReducers;
